// CPI-1211: the twelve file_type values BMS (mhub-book-api) stores on shared_medias, mapped to
// their i18n keys. The four *_form / receipt types are written server-side by book-api when a
// sales recommendation is approved — nobody uploads them — and the CRM offers download only.
// Keep this in step with models.ValidFileTypes in mhub-book-api and with validFileTypes in
// mhub-lead-integrations; the three lists are the same list.
export const FILE_TYPE_LABEL_KEYS = {
  bank_loan_document: 'component.label.bank_loan_document',
  bank_package_form: 'component.label.bank_package_form',
  bank_package_image: 'component.label.bank_package_image',
  banker_opportunity_form: 'component.label.banker_opportunity_form',
  identity: 'component.label.identity_document',
  installment_payment: 'component.label.installment_payment',
  opportunity_form: 'component.label.opportunity_form',
  other_documents: 'component.label.other_documents',
  registration_of_interest: 'component.label.registration_of_interest',
  token_payment: 'component.label.token_payment',
  token_payment_receipt: 'component.label.token_payment_receipt',
  token_refund: 'component.label.token_refund',
};

/**
 * Human label for a shared-media file_type.
 *
 * An unknown type falls back to the raw value rather than to a blank or to another type's
 * label: BMS can gain a file_type before the CRM knows about it, and showing "opportunity_form"
 * is honest where showing "Bank loan document" would be a lie.
 *
 * @param {(key: string, fallback: string) => string} t  i18next's t
 * @param {string} fileType
 * @returns {string}
 */
export function fileTypeLabel(t, fileType) {
  if (!fileType) return '-';
  const key = FILE_TYPE_LABEL_KEYS[fileType];
  if (!key) return fileType;
  return t(key, fileType);
}

// CPI-1211: the only file_type the CRM's Documents section lists. banker_opportunity_form is
// deliberately excluded — it is the bank's copy of the same document, not the buyer's — so
// the filter is an exact match on one string, never a prefix.
export const OPPORTUNITY_FORM_FILE_TYPE = 'opportunity_form';

export const IMAGE_EXTENSION_RE = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i;

/**
 * The rows this section shows, out of every shared media on the opportunity.
 *
 * Lives here rather than in DocumentsSection.jsx so a test can pin it without importing the app
 * root (the component pulls in the Apollo client, which starts Bugsnag): getting it wrong either
 * hides the buyer's form or leaks the bank's copy onto the buyer's screen.
 *
 * @param {Array<{ file_type?: string }>} medias
 * @returns {Array<object>}
 */
export function selectOpportunityFormDocuments(medias) {
  if (!Array.isArray(medias)) return [];
  return medias.filter((m) => m && m.file_type === OPPORTUNITY_FORM_FILE_TYPE);
}

/**
 * Whether a document previews as an image (in FilePreviewModal) rather than downloading.
 * Trusts the content type when BMS supplied one and falls back to the file extension.
 *
 * @param {{ file_content_type?: string, file_name?: string }} doc
 * @returns {boolean}
 */
export function isImageDocument(doc) {
  if (!doc) return false;
  if (doc.file_content_type) return doc.file_content_type.startsWith('image/');
  return IMAGE_EXTENSION_RE.test(doc.file_name || '');
}

const CACHE_KEY = (source, mediaId) => `media_${source}_${mediaId}`;
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

/**
 * Upload a file directly to S3 via presigned PUT URL.
 * Does NOT use Amplify — sends a raw fetch PUT with Content-Type and Content-Length headers.
 */
export async function uploadToS3(presignedUrl, file) {
  let response;
  try {
    response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
  } catch (networkError) {
    // CORS block or network failure — browser never got a response
    throw new Error(`S3 upload network error (likely CORS): ${networkError.message}`);
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}. ${body}`.trim());
  }
}

/**
 * Get a presigned view URL for a media item, with sessionStorage caching.
 * Re-fetches when the cached URL is within 2 minutes of expiry.
 *
 * @param {string} source  - Cache namespace (e.g. 'crm')
 * @param {string} mediaId - The shared_media_id
 * @param {() => Promise<{ presigned_url: string, expires_at: number }>} fetchFn
 *   - Called to fetch a fresh URL; expires_at is a Unix timestamp in seconds
 * @returns {Promise<string>} The presigned URL
 */
export async function getMediaUrl(source, mediaId, fetchFn) {
  const cacheKey = CACHE_KEY(source, mediaId);
  const cached = sessionStorage.getItem(cacheKey);

  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
        return parsed.presignedUrl;
      }
    } catch {
      // corrupted cache — fall through to fetch
    }
  }

  const result = await fetchFn();
  const entry = {
    presignedUrl: result.presigned_url,
    expiresAt: result.expires_at * 1000,
  };
  sessionStorage.setItem(cacheKey, JSON.stringify(entry));
  return result.presigned_url;
}

/**
 * Fetch a URL as a blob and return an object URL for download/display.
 * Caller is responsible for revoking the object URL when done.
 */
export async function fetchFileAsBlob(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Extract the S3 bucket name from a presigned PUT URL.
 *
 * The presigned-upload response only returns { id, object_key, presigned_url };
 * it never returns the bucket. But the file lands in book-api's OWN bucket
 * (not any FE env bucket), and the only place that bucket name appears on the
 * FE is inside the presigned URL. So a follow-up createSharedMediaFromS3 must
 * read the bucket back out of the URL — passing a hardcoded env bucket makes
 * book-api HeadObject the wrong bucket and fail with "source file not found".
 *
 * Handles both AWS URL styles (book-api's default is virtual-hosted):
 *   virtual-hosted: https://<bucket>.s3.<region>.amazonaws.com/<key>?...
 *   path-style:     https://s3.<region>.amazonaws.com/<bucket>/<key>?...
 *
 * @param {string} presignedUrl
 * @returns {string|null} bucket name, or null if it can't be parsed
 */
export function bucketFromPresignedUrl(presignedUrl) {
  if (!presignedUrl) return null;
  try {
    const { hostname, pathname } = new URL(presignedUrl);
    // Path-style: host is s3.<region>.amazonaws.com, bucket is first path seg.
    if (/^s3[.-]/i.test(hostname)) {
      return pathname.replace(/^\/+/, '').split('/')[0] || null;
    }
    // Virtual-hosted: bucket is the label before ".s3." / ".s3-".
    const match = hostname.match(/^(.+?)\.s3[.-]/i);
    return match ? match[1] : null;
  } catch (e) {
    return null;
  }
}

// IC media fields may be stored as plain objects or DynamoDB-shaped values.
const extractMediaKey = (field) => field?.M?.key?.S || field?.key || null;
const extractMediaBucket = (field) => field?.M?.bucket?.S || field?.bucket || null;
const extractMediaName = (field) => field?.M?.name?.S || field?.name || null;
const stripPrivatePrefix = (raw) => (raw ? raw.replace(/^\/?private\//, '') : raw);

const fileExtension = (name, key) => {
  const source = name || key || '';
  const ext = source.includes('.') ? source.split('.').pop().toLowerCase() : '';
  return ext || 'jpg';
};

// Short stable hash (djb2) of the source key, embedded in the mirror file name so
// it changes whenever the purchaser replaces the underlying S3 object (new key →
// new hash → new name). That lets the backend create the new copy and supersede
// the stale one, while an unchanged image reuses the name and is a no-op.
const hashSourceKey = (key) => {
  let h = 5381;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 33 + key.charCodeAt(i)) % 4294967296;
  }
  return h.toString(16);
};

// Cap how many opportunities are mirrored at once. Each opportunity fans out to
// up to two backend mutations (front + back), each of which copies an S3 object,
// so an unbounded burst for a customer with many bookings is a reliability risk.
const OPPORTUNITY_MIRROR_BATCH_SIZE = 4;

/**
 * Push a purchaser's CCCD identity images (front/back) to the opportunity's
 * shared media so they surface in the Documents tab. Fire-and-forget: never
 * throws and never blocks the caller. The mirrored file name is
 * "identity_<1|2>_<customerId>_<hash>.<ext>" (1 = front, 2 = back): the prefix is
 * stable per purchaser+side and the hash tracks the source object, so replacing a
 * CCCD yields a new name and the backend supersedes the stale copy; unchanged = no-op.
 *
 * @param {(opts: { variables: object }) => Promise<any>} pushFromS3
 *   The bound createSharedMediaFromS3 mutate function.
 * @param {string} opportunityId
 * @param {Array<{ customerId: string, contact: object }>} purchasers
 */
export async function pushIdentityDocuments(pushFromS3, opportunityId, purchasers, uploader = {}) {
  if (!pushFromS3 || !opportunityId || !Array.isArray(purchasers)) return;

  const tasks = [];
  purchasers.forEach(({ customerId, contact }) => {
    if (!customerId || !contact) return;
    [['ic_front', 'front'], ['ic_back', 'back']].forEach(([fieldName, side]) => {
      const field = contact[fieldName];
      const key = extractMediaKey(field);
      if (!key) return;
      const bucket = extractMediaBucket(field) || process.env.REACT_APP_SRB_S3;
      if (!bucket) return;
      const ext = fileExtension(extractMediaName(field), key);
      const sourceKey = stripPrivatePrefix(key);
      // The mirror file name embeds a hash of the source key: the
      // identity_<1|2>_<customerId> prefix (1 = front, 2 = back) is stable per
      // purchaser+side so the backend supersedes an old copy, while the hash changes
      // when the image changes so a replacement is a new copy. identityKeyPrefix() parses it.
      const fileName = `identity_${side === 'front' ? '1' : '2'}_${customerId}_${hashSourceKey(sourceKey)}.${ext}`;
      tasks.push(
        pushFromS3({
          variables: {
            parent_type: 'opportunity',
            parent_id: opportunityId,
            source_bucket: bucket,
            source_key: sourceKey,
            file_name: fileName,
            file_type: 'identity',
            ...uploader,
          },
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('pushIdentityDocuments failed', fieldName, customerId, err?.message);
        }),
      );
    });
  });
  await Promise.allSettled(tasks);
}

/**
 * Fan a single purchaser's CCCD identity images out to every opportunity they
 * belong to, so replacing a CCCD refreshes the Documents tab on all of their
 * bookings (not just newly created ones). Each opportunity is delegated to
 * pushIdentityDocuments, which mirrors front/back and de-duplicates by file name:
 * a changed image yields a new hash so the backend supersedes the stale copy,
 * while an unchanged image is a no-op. Never throws (each push catches and
 * logs its own errors). Safe to fire-and-forget; awaiting resolves once every
 * push has settled. Duplicate ids are collapsed and work runs in bounded batches
 * to avoid a request burst.
 *
 * @param {(opts: { variables: object }) => Promise<any>} pushFromS3
 *   The bound createSharedMediaFromS3 mutate function.
 * @param {string[]} opportunityIds
 * @param {string} customerId
 * @param {object} contact - the purchaser's contact with ic_front / ic_back.
 * @param {object} [uploader] - extra uploader fields (e.g. uploader_user_aws_id).
 */
export async function pushIdentityToOpportunities(
  pushFromS3,
  opportunityIds,
  customerId,
  contact,
  uploader = {},
) {
  if (!pushFromS3 || !customerId || !contact || !Array.isArray(opportunityIds)) return;
  const purchasers = [{ customerId, contact }];
  const uniqueIds = Array.from(new Set(opportunityIds.filter(Boolean)));
  for (let i = 0; i < uniqueIds.length; i += OPPORTUNITY_MIRROR_BATCH_SIZE) {
    const batch = uniqueIds.slice(i, i + OPPORTUNITY_MIRROR_BATCH_SIZE);
    // eslint-disable-next-line no-await-in-loop
    await Promise.allSettled(
      batch.map((opportunityId) => (
        pushIdentityDocuments(pushFromS3, opportunityId, purchasers, uploader)
      )),
    );
  }
}

/**
 * The kind of file a name denotes, for picking its icon and icon colour.
 *
 * Mirrors getFileIcon/getFileIconColor in mhub-book-web's AttachmentItem so the same document
 * carries the same icon in the Showroom and in the CRM (CPI-1211 mock-up). The mapping is
 * returned as a kind rather than as an icon component so it stays a pure function this file can
 * be tested without a renderer — the kind is resolved to an icon in AttachmentItem.jsx.
 *
 * @param {string} fileName
 * @returns {'pdf'|'image'|'word'|'excel'|'powerpoint'|'text'|'other'}
 */
export function fileIconKind(fileName) {
  const name = typeof fileName === 'string' ? fileName : '';
  // No dot means no extension: String.split always yields at least one element, so guard on the
  // dot rather than trusting pop() — otherwise "Interest_Expression" reads as its own extension.
  if (!name.includes('.')) return 'other';
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff'].includes(ext)) return 'image';
  if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) return 'word';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
  if (ext === 'txt') return 'text';
  return 'other';
}

// A leading v4-style id followed by an underscore, as mhub-book-api's file names begin.
const LEADING_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;

/**
 * The file name to show for a generated document, without the parent id it is prefixed with.
 *
 * mhub-book-api names these `<opportunityID>_<templateName>.pdf`
 * (handlers/opportunities.go:1929), so every document on one booking carries the same 37-character
 * prefix. Shown whole in a sidebar the prefix is all that fits, and two different forms become
 * indistinguishable rows; the part that names the document is the tail. The stored name is
 * unchanged — it is still what the download and the row's hover text use.
 *
 * The prefix is removed only when it really is this booking's id (or, with no parentId to hand,
 * something shaped exactly like one), and never when removing it would leave nothing.
 *
 * @param {string} fileName
 * @param {string} [parentId]  the opportunity id the document hangs off
 * @returns {string}
 */
export function documentDisplayName(fileName, parentId) {
  if (typeof fileName !== 'string' || !fileName) return '';
  let stripped = null;
  if (parentId && fileName.startsWith(`${parentId}_`)) {
    stripped = fileName.slice(parentId.length + 1);
  } else if (LEADING_ID_RE.test(fileName)) {
    stripped = fileName.replace(LEADING_ID_RE, '');
  }
  return stripped || fileName;
}
